import { useFormConfigContext } from "../../../common/contexts/FormConfigContext";
import { useFormContext } from "../../../common/contexts/FormContext";
import { useSharedStateWithActions } from "../../../common/hooks/useSharedState";
import {
  AppConstants,
  conceptIDs,
  MLY_IDS,
  sharedStateKeys,
  stepTypes,
} from "../../../constants/constant";
import {
  BrandDetails,
  CalculateEstimateResponse,
  FranchiseLookupResponse,
  Step,
} from "../../../Interface/scheduler/form";
import {
  calculateEstimate,
  fetchAddressSuggestions,
  fetchSchedulingByFullAttribute,
  getIsExistingCustomer,
  getLocalMenuHeaderLogo,
} from "../../../service/apiService";
import {
  getCPEstimateFlag,
  isInvalidCityState,
  renderUpsellValue,
  skipMLYCalendar,
} from "../../../utility/utility";
import { useFeeInspection } from "./useFeeInspection";
import cloneDeep from "lodash.clonedeep";

export const useFranchiseLookup = () => {
  const { state } = useSharedStateWithActions();
  const { formValues, setFormValues } = useFormContext();
  const { initialFormConfig, formConfig, setFormConfig } =
    useFormConfigContext();
  const { handleFeeInspection } = useFeeInspection();
  const id = state[sharedStateKeys.MODIFIER_FLOW_ID];

  const handleFranchiseLookup = async (
    res: FranchiseLookupResponse[],
    brandDetails: BrandDetails,
    isNearByLocation = false,
  ) => {
    if (!res || res.length === 0) return;
    let customer;
    let isMLYRes: boolean;
    let isMLYJSONUndefined: boolean;
    let estRes: CalculateEstimateResponse;
    let mlyEstimate: string;
    let isMLYEstimate = false;
    let missingCityStateData = {
      city: formValues.city,
      state: formValues.state,
    };
    let isOneTimeCleaning = false;
    let isMoveInOutCleaning = false;
    let hideMLYEstimateSection = false;

    const {
      franchiseId,
      licenseNumber,
      franchiseWebLocationId,
      doingBusinessAs,
    } = res[0];

    localStorage.setItem("weblocationId", franchiseWebLocationId);

    if (
      formValues.zipcode &&
      isInvalidCityState(formValues.city, formValues.state)
    ) {
      const addressQuery = `${formValues?.address ?? ""} ${formValues.zipcode}`;
      const addressSuggestions = await fetchAddressSuggestions(
        "address",
        addressQuery,
        formValues.zipcode,
        setFormValues,
        () => {},
      );
      const suggestedAddress = addressSuggestions?.[0].address;
      missingCityStateData = {
        city: suggestedAddress?.city,
        state: suggestedAddress?.state,
      };
    }

    // Fetch and update local header logo if applicable
    const localHeaderLogo =
      brandDetails?.displayDynamicLogo &&
      (await getLocalMenuHeaderLogo(franchiseWebLocationId));
    if (localHeaderLogo && typeof globalThis?.UpdateHeaderLogo === "function") {
      globalThis.UpdateHeaderLogo(localHeaderLogo);
    }

    // Localization
    if (
      typeof globalThis?.localizationHelper === "function" &&
      !isNearByLocation
    ) {
      globalThis?.localizationHelper(res);
    }

    // Save booking lead sent
    sessionStorage.setItem("bookingLeadSent", "true");

    // Fetch online scheduling details
    const onlineSchedulingRes = await fetchSchedulingByFullAttribute(
      franchiseWebLocationId,
    );

    if (
      formConfig.conceptid === conceptIDs.MLY_CONCEPT_ID &&
      formValues?.[MLY_IDS.SERVICE] === MLY_IDS.RESIDENTIAL
    ) {
      const upsellData = await renderUpsellValue(formConfig.conceptCode);
      isMLYRes = true;
      isMLYJSONUndefined =
        upsellData?.[formValues?.[MLY_IDS.FREQUENCY]] === undefined;
    }

    // Check if it is already Exisitng Customer
    const payloadObject = {
      firstName: formValues.fname,
      lastName: formValues.lname,
      email: formValues.email,
      phone: ("" + formValues.phone).replaceAll(/\D/g, ""),
      zipcode: formValues.zipcode,
      conceptId: Number(formConfig.conceptid),
      weblocationId: Number(franchiseWebLocationId),
    };
    if (formConfig.conceptid === conceptIDs.WDG_CONCEPT_ID)
      customer = await getIsExistingCustomer(payloadObject);

    sessionStorage.setItem(
      "alternateConcept",
      onlineSchedulingRes?.alternateConcept,
    );

    if (formConfig.feeDetails?.displayInspectionFee) {
      sessionStorage.setItem(
        "showFees",
        onlineSchedulingRes?.options?.showFees,
      );
      handleFeeInspection(franchiseWebLocationId);
    }

    const { onlineScheduling, options } = onlineSchedulingRes;
    const shouldAutoSelectService =
      String(formConfig?.conceptid) === "26" &&
      options?.offersClosetAppointments === false;
    let mlyOneTimeFlow = false;
    hideMLYEstimateSection =
      getCPEstimateFlag(formConfig) ||
      options?.mollyOneTimeCleansDisableEstimatePricing ||
      !options?.optInForPOSScheduling2;

    if (formValues?.[MLY_IDS.FREQUENCY] === MLY_IDS.ONE_TIME_CLEAN) {
      mlyOneTimeFlow = true;
      const isCleanType = formValues[MLY_IDS.ONE_TIME_CLEAN_ID];
      estRes = await calculateEstimate(
        { ...formValues, franchiseWebLocationId },
        formConfig,
        missingCityStateData,
      );

      if (isCleanType === MLY_IDS.MOVE_IN_OUT_CLEANING_OPTION) {
        if (estRes?.estimateMoveRangeLow && estRes?.estimateMoveRangeHigh) {
          isMLYEstimate = true;
          mlyEstimate = `$${Math.round(estRes.estimateMoveRangeLow)} - $${Math.round(estRes.estimateMoveRangeHigh)}`;
          isMoveInOutCleaning = true;
        }
      }

      if (isCleanType === MLY_IDS.ONE_TIME_CLEANING_OPTION) {
        if (
          estRes?.estimateOccasionalRangeLow &&
          estRes?.estimateOccasionalRangeHigh
        ) {
          isMLYEstimate = true;
          mlyEstimate = `$${Math.round(estRes.estimateOccasionalRangeLow)} - $${Math.round(estRes.estimateOccasionalRangeHigh)}`;
          isOneTimeCleaning = true;
        }
      }
    }

    const getCalendarFlow = () => {
      if (mlyOneTimeFlow) return options?.optInForPOSScheduling2;
      else return onlineScheduling;
    };

    // Update form values
    setFormValues((prevValues) => ({
      ...prevValues,
      franchiseId,
      licenseNumber,
      franchiseWebLocationId,
      city: missingCityStateData.city,
      state: missingCityStateData.state,
      conceptID: formConfig.conceptid,
      dbaName: doingBusinessAs,
      onlineScheduling,
      isCalendarDisabled: !options?.optInForPOSScheduling2,
      hideCESEstimation: options?.mollyOneTimeCleansDisableEstimatePricing,
      priceQuoteEstimateAvailable: options?.optInForPriceQuoteEstimate,
      offersClosetAppointments: options?.offersClosetAppointments ?? false,
      ...(shouldAutoSelectService && {
        [AppConstants.SHELF_STORAGE_ID]: AppConstants.SHELF_CLOSET_DEFAULT,
      }),
      legalText:
        formConfig.steps[formValues.currentStep + 1]?.subTitle ||
        (formConfig.conceptid === conceptIDs.LAP_CONCEPT_ID &&
          formConfig?.steps[formValues.currentStep + 1]?.steptype ===
            "service-bot-section"),
      service: "",
      servicecategory: "",
      serviceCategoryName: "",
      serviceName: "",
      scheduleId:
        onlineScheduling && prevValues.scheduleId ? prevValues.scheduleId : "",
      date: onlineScheduling && prevValues.date ? prevValues.date : "",
      slot: onlineScheduling && prevValues.slot ? prevValues.slot : "",
      existingcustomer: customer?.isReturnCustomer ? "true" : "false",
      ...(isMLYRes && {
        isMLYJSONUndefined,
      }),
      ...(isMLYEstimate && {
        isMLYEstimate,
        mlyEstimate,
        calculateEstimateRes: estRes,
        isOneTimeCleaning,
        hideMLYEstimateSection,
        isMoveInOutCleaning,
      }),
    }));

    const updatedFormConfig = cloneDeep(initialFormConfig);
    if (!updatedFormConfig) return;

    // Remove steps if no online scheduling or near by location selected
    let shouldRemoveRequestTime =
      (formConfig.conceptid !== conceptIDs.MLY_CONCEPT_ID &&
        !onlineScheduling) ||
      isNearByLocation ||
      (formConfig.conceptid === conceptIDs.MLY_CONCEPT_ID &&
        skipMLYCalendar({
          ...formValues,
          isCalendarDisabled: !options?.optInForPOSScheduling2,
          onlineScheduling: onlineScheduling,
        }));

    // SHG-specific: when closet appointments are explicitly disabled,
    // keep the request-time section in the flow.
    if (
      String(formConfig?.conceptid) === "26" &&
      options?.offersClosetAppointments === false
    ) {
      shouldRemoveRequestTime = false;
    }

    if (shouldRemoveRequestTime) {
      updatedFormConfig.steps = updatedFormConfig.steps?.filter(
        (step: Step) => step.steptype !== AppConstants.REQUEST_TIME_TYPE,
      );
    }

    // Modifier flow for MDG and PLG
    if (id) {
      sessionStorage.setItem(
        AppConstants.IS_AUTO_FLOW,
        id === AppConstants.RESIDENTIAL_TEXT?.toLowerCase() ||
          id === AppConstants.COMMERCIAL_TEXT
          ? "false"
          : "true",
      );

      if (id === AppConstants.RESIDENTIAL_TEXT?.toLowerCase()) {
        updatedFormConfig?.steps?.forEach((step: Step) => {
          if (step.steptype === AppConstants.HOW_WE_CAN_REACH) {
            step.elements?.forEach((element) => {
              if (element.type === AppConstants.AGNOSTIC_FORM) {
                element.fields = element.fields?.filter(
                  (field) => field.id !== AppConstants.BUSINESS_NAME,
                );
              }
            });
          }
        });

        updatedFormConfig.steps = updatedFormConfig.steps?.filter(
          (step: Step) =>
            step.steptype !== AppConstants.SERVICE_INFORMATION &&
            step.steptype !== AppConstants.VEHICLE_INFORMATION_SECTION,
        );
      } else if (id === AppConstants.COMMERCIAL_TEXT) {
        updatedFormConfig.steps = updatedFormConfig.steps?.filter(
          (step: Step) =>
            step.steptype !== AppConstants.SERVICE_INFORMATION &&
            step.steptype !== AppConstants.VEHICLE_INFORMATION_SECTION,
        );
      } else {
        updatedFormConfig.steps = updatedFormConfig.steps?.filter(
          (step: Step) => step.steptype !== AppConstants.HOW_WE_CAN_REACH,
        );
      }
    }

    // Remove price quote estimation if not opted in
    if (!options?.optInForPriceQuoteEstimate || customer?.isReturnCustomer) {
      updatedFormConfig.steps = updatedFormConfig.steps?.filter(
        (step: Step) => step.steptype !== stepTypes.STEP_PRICE_QUOTE_ESTIMATION,
      );
    }

    setFormConfig(updatedFormConfig);
    return [
      isNearByLocation ? false : getCalendarFlow(),
      mlyEstimate,
      shouldAutoSelectService,
    ];
  };

  return { handleFranchiseLookup };
};
